import React, { useState, useMemo, useRef, useEffect } from 'react';
import { T, amountStyle } from '../theme';
import { X, CheckCircle2, ChevronDown } from 'lucide-react';

function itemKey(item) {
  return `${item.source_type}::${item.filename}::${item.id}`;
}

function applySort(list, sort) {
  return [...list].sort((a, b) => {
    if (sort === 'date-desc')   return (b.date ?? '') > (a.date ?? '') ? 1 : -1;
    if (sort === 'date-asc')    return (a.date ?? '') > (b.date ?? '') ? 1 : -1;
    if (sort === 'amount-desc') return Math.abs(b.amount) - Math.abs(a.amount);
    if (sort === 'amount-asc')  return Math.abs(a.amount) - Math.abs(b.amount);
    if (sort === 'vendor-asc')  return (a.vendor ?? '').localeCompare(b.vendor ?? '');
    if (sort === 'vendor-desc') return (b.vendor ?? '').localeCompare(a.vendor ?? '');
    return 0;
  });
}

function applySearch(list, q) {
  if (!q) return list;
  const s = q.toLowerCase();
  return list.filter(i =>
    (i.vendor ?? '').toLowerCase().includes(s) ||
    (i.source ?? '').toLowerCase().includes(s)
  );
}

const SORT_OPTIONS = [
  { value: 'date-desc',   label: 'Date ↓' },
  { value: 'date-asc',    label: 'Date ↑' },
  { value: 'amount-desc', label: 'Amount ↓' },
  { value: 'amount-asc',  label: 'Amount ↑' },
  { value: 'vendor-asc',  label: 'Vendor A–Z' },
  { value: 'vendor-desc', label: 'Vendor Z–A' },
];

function SortFilterBar({ search, onSearch, sort, onSort }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        type="text"
        placeholder="Search…"
        value={search}
        onChange={e => onSearch(e.target.value)}
        style={{
          flex: 1, fontSize: 11, padding: '4px 8px', minWidth: 0,
          border: `1px solid ${T.tealPale}`, borderRadius: 6,
          outline: 'none', color: '#374151',
        }}
      />
      <select value={sort} onChange={e => onSort(e.target.value)}
        style={{
          fontSize: 11, padding: '4px 4px',
          border: `1px solid ${T.tealPale}`, borderRadius: 6,
          background: '#fff', color: '#374151', cursor: 'pointer',
        }}>
        {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

const BTN_BASE   = { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#fff', flexShrink: 0, border: 'none', cursor: 'pointer' };
const BTN_MARK   = { ...BTN_BASE };
const BTN_UNMARK = { ...BTN_BASE, border: '1px solid #D1D5DB', color: '#6B7280' };

const FLUSH_DELAY = 10_000;

export default function ReimbursementModal({
  items = [],
  grossOwed = 0,
  defaultCurrency = 'USD',
  targetAmount,
  onMark,
  onClose,
}) {
  const [locallyMarked,   setLocallyMarked]   = useState(new Set());
  const [locallyUnmarked, setLocallyUnmarked] = useState(new Set());
  const [flushing, setFlushing]               = useState(false);
  const [reimbursedExpanded, setReimbursedExpanded] = useState(false);
  const [selectedKeys, setSelectedKeys]       = useState(new Set());

  const [expSearch, setExpSearch]     = useState('');
  const [expSort, setExpSort]         = useState('date-desc');
  const [reimbSearch, setReimbSearch] = useState('');
  const [reimbSort, setReimbSort]     = useState('date-desc');

  // Pending changes: itemKey → { item, value } — only the latest value per item is kept.
  const pendingRef = useRef(new Map());
  const timerRef   = useRef(null);
  // Keep onMark in a ref so the timeout callback always calls the current version.
  const onMarkRef  = useRef(onMark);
  onMarkRef.current = onMark;

  const effectiveItems = useMemo(() => {
    if (locallyMarked.size === 0 && locallyUnmarked.size === 0) return items;
    return items.map(i => {
      const key = itemKey(i);
      if (locallyMarked.has(key))   return { ...i, reimbursed: true };
      if (locallyUnmarked.has(key)) return { ...i, reimbursed: false };
      return i;
    });
  }, [items, locallyMarked, locallyUnmarked]);

  // Mirrors grandTotal formula: refund amounts are negative so a plain sum gives
  // the correct net (reimbursed expenses minus reimbursed refunds).
  const localReimbursedTotal = useMemo(() =>
    parseFloat(effectiveItems.filter(i => i.reimbursed).reduce((s, i) => s + i.amount, 0).toFixed(2)),
  [effectiveItems]);

  const outstanding = parseFloat((grossOwed - localReimbursedTotal).toFixed(2));
  const settled     = outstanding <= 0 && localReimbursedTotal > 0;

  const unreimbursed = useMemo(() => effectiveItems.filter(i => !i.reimbursed), [effectiveItems]);
  const reimbursed   = useMemo(() => effectiveItems.filter(i =>  i.reimbursed), [effectiveItems]);
  const expenses     = useMemo(() => unreimbursed.filter(i => !i.is_refund),    [unreimbursed]);
  const refunds      = useMemo(() => unreimbursed.filter(i =>  i.is_refund),    [unreimbursed]);

  const displayedExpenses   = useMemo(() => applySort(applySearch(expenses,  expSearch),   expSort),   [expenses,  expSearch,  expSort]);
  const displayedReimbursed = useMemo(() => applySort(applySearch(reimbursed, reimbSearch), reimbSort), [reimbursed, reimbSearch, reimbSort]);

  // Drop stale selections when items leave the outstanding list (after being marked).
  useEffect(() => {
    const expenseKeySet = new Set(expenses.map(itemKey));
    setSelectedKeys(prev => {
      const filtered = [...prev].filter(k => expenseKeySet.has(k));
      return filtered.length === prev.size ? prev : new Set(filtered);
    });
  }, [expenses]);

  // Auto-select expenses up to targetAmount (oldest-first) when opening from a pending payment.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!targetAmount || targetAmount <= 0) return;
    const sorted = [...expenses].sort((a, b) => (a.date ?? '') < (b.date ?? '') ? -1 : 1);
    let running = 0;
    const keys = new Set();
    for (const item of sorted) {
      if (running >= targetAmount) break;
      keys.add(itemKey(item));
      running = parseFloat((running + item.amount).toFixed(2));
    }
    if (keys.size > 0) setSelectedKeys(keys);
  }, []); // run once on mount

  const selectedItems = useMemo(() =>
    expenses.filter(i => selectedKeys.has(itemKey(i))),
  [expenses, selectedKeys]);

  const selectedNet = useMemo(() =>
    parseFloat(selectedItems.reduce((s, i) => s + i.amount, 0).toFixed(2)),
  [selectedItems]);

  // ── Batched Firestore flush ────────────────────────────────────────────────

  async function doFlush() {
    timerRef.current = null;
    if (pendingRef.current.size === 0) return;

    const changes  = [...pendingRef.current.values()];
    pendingRef.current = new Map(); // clear before async so concurrent enqueues go to next batch

    const toMark   = changes.filter(c =>  c.value).map(c => c.item);
    const toUnmark = changes.filter(c => !c.value).map(c => c.item);

    setFlushing(true);
    try {
      // Sequential writes: same-file batches inside each onMark call, no concurrent races.
      if (toMark.length > 0)   await onMarkRef.current(toMark,   true);
      if (toUnmark.length > 0) await onMarkRef.current(toUnmark, false);
    } catch { /* local state is already correct; silent failure */ }
    finally { setFlushing(false); }
  }

  function enqueue(item, value) {
    pendingRef.current.set(itemKey(item), { item, value });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(doFlush, FLUSH_DELAY);
  }

  // Flush pending writes when the modal closes so nothing is lost.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    doFlush(); // fire-and-forget; component is unmounting
  }, []);

  // ── Change handlers ───────────────────────────────────────────────────────

  function handleUnmarkItem(item) {
    const key = itemKey(item);
    setLocallyUnmarked(prev => new Set([...prev, key]));
    setLocallyMarked(prev => { const n = new Set(prev); n.delete(key); return n; });
    enqueue(item, false);
  }

  function handleMarkItem(item) {
    const key = itemKey(item);
    setLocallyMarked(prev => new Set([...prev, key]));
    setLocallyUnmarked(prev => { const n = new Set(prev); n.delete(key); return n; });
    enqueue(item, true);
  }

  function toggleSelect(item) {
    const key = itemKey(item);
    setSelectedKeys(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  }

  function selectAll() {
    setSelectedKeys(prev => new Set([...prev, ...displayedExpenses.map(itemKey)]));
  }

  function deselectAll() {
    setSelectedKeys(new Set());
  }

  async function handleMarkSelected() {
    if (selectedItems.length === 0) return;
    const toMark = [...selectedItems];
    const keys   = toMark.map(itemKey);
    setSelectedKeys(new Set());
    setLocallyMarked(prev => new Set([...prev, ...keys]));
    setLocallyUnmarked(prev => { const n = new Set(prev); keys.forEach(k => n.delete(k)); return n; });
    toMark.forEach(item => pendingRef.current.set(itemKey(item), { item, value: true }));
    if (timerRef.current) clearTimeout(timerRef.current);
    await doFlush(); // flush immediately for deliberate bulk action
  }

  const fmt = n => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: `1px solid ${T.tealPale}` }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 700, fontSize: 17, color: T.navy }}>Reimbursement Tracker</span>
            <span style={{ fontSize: 12, color: '#64748b' }}>Business to Personal</span>
            {flushing && <span style={{ fontSize: 11, color: '#94a3b8' }}>Saving…</span>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color={T.navy} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Balance summary */}
          <div style={{ background: settled ? '#F0FDF4' : '#EFF6FF', borderRadius: 12, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {settled && <CheckCircle2 size={16} color="#16a34a" />}
              <span style={{ fontWeight: 700, fontSize: 14, color: settled ? '#16a34a' : T.navy }}>
                {settled ? 'All settled' : `Outstanding: ${defaultCurrency} ${fmt(outstanding)}`}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              {defaultCurrency} {fmt(grossOwed)} owed · {defaultCurrency} {fmt(localReimbursedTotal)} marked reimbursed
            </div>
          </div>

          {/* Unreimbursed credits / refunds */}
          {refunds.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Credits / Refunds</span>
              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid #bbf7d0', borderRadius: 10, padding: '6px 8px', background: '#F0FDF4' }}>
                {refunds.map(item => (
                  <div key={itemKey(item)} style={{ display: 'flex', alignItems: 'center', gap: 12, borderRadius: 8, padding: '7px 8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.vendor || '(no vendor)'}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.date} · {item.source}</div>
                    </div>
                    <div style={{ ...amountStyle(), fontSize: 13, fontWeight: 700, color: '#16a34a', flexShrink: 0, marginRight: 8 }}>
                      −{defaultCurrency} {fmt(item.amount)}
                    </div>
                    <button onClick={() => handleMarkItem(item)}
                      style={{ ...BTN_MARK, border: '1px solid #16a34a', color: '#16a34a' }}>
                      Mark
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outstanding expenses */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Outstanding Expenses
              </span>
              {expenses.length > 0 && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={selectAll} style={{ fontSize: 11, color: T.tealDeep, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                    Select all
                  </button>
                  {selectedKeys.size > 0 && (
                    <button onClick={deselectAll} style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      Deselect all
                    </button>
                  )}
                </div>
              )}
            </div>
            <SortFilterBar search={expSearch} onSearch={setExpSearch} sort={expSort} onSort={setExpSort} />
            <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, border: `1px solid ${T.tealPale}`, borderRadius: 10, padding: '6px 8px' }}>
              {displayedExpenses.map(item => {
                const key = itemKey(item);
                const checked = selectedKeys.has(key);
                return (
                  <div key={key} onClick={() => toggleSelect(item)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, borderRadius: 8, padding: '7px 8px', cursor: 'pointer', background: checked ? '#F0F9FF' : 'transparent' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleSelect(item)}
                      onClick={e => e.stopPropagation()}
                      style={{ width: 15, height: 15, accentColor: T.tealDeep, flexShrink: 0, cursor: 'pointer' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.vendor || '(no vendor)'}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.date} · {item.source}</div>
                    </div>
                    <div style={{ ...amountStyle(), fontSize: 13, fontWeight: 700, color: '#111', flexShrink: 0 }}>
                      {defaultCurrency} {fmt(item.amount)}
                    </div>
                  </div>
                );
              })}
              {displayedExpenses.length === 0 && (
                <div style={{ padding: '10px 8px', color: '#94a3b8', fontSize: 12 }}>
                  {expenses.length === 0 ? 'No outstanding expenses.' : 'No results.'}
                </div>
              )}
            </div>
          </div>

          {/* Mark Selected button */}
          {selectedItems.length > 0 && (
            <button onClick={handleMarkSelected} disabled={flushing}
              style={{
                background: T.gradTeal, color: '#fff', border: 'none', borderRadius: 8,
                padding: '8px 16px', fontWeight: 700, fontSize: 12, alignSelf: 'flex-start',
                cursor: flushing ? 'default' : 'pointer', opacity: flushing ? 0.6 : 1,
              }}>
              {flushing ? 'Saving…' : `Mark ${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''} as Reimbursed · ${defaultCurrency} ${fmt(selectedNet)}`}
            </button>
          )}

          {/* Already-reimbursed items — collapsible */}
          {reimbursed.length > 0 && (
            <div>
              <button onClick={() => setReimbursedExpanded(v => !v)} style={{
                display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none',
                cursor: 'pointer', padding: '4px 0', color: '#6B7280', fontSize: 12, fontWeight: 700,
              }}>
                <ChevronDown size={13} style={{ transition: 'transform 0.2s', transform: reimbursedExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                Already reimbursed ({reimbursed.length})
              </button>
              {reimbursedExpanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                  <SortFilterBar search={reimbSearch} onSearch={setReimbSearch} sort={reimbSort} onSort={setReimbSort} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {displayedReimbursed.map(item => (
                      <div key={itemKey(item)} style={{ display: 'flex', alignItems: 'center', gap: 12, background: item.is_refund ? '#F0FDF4' : '#F8FAFC', borderRadius: 10, padding: '8px 12px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.vendor || '(no vendor)'}</span>
                            {item.is_refund && (
                              <span style={{ fontSize: 9, fontWeight: 800, color: '#15803d', background: '#DCFCE7', borderRadius: 4, padding: '1px 5px', letterSpacing: 0.3, textTransform: 'uppercase', flexShrink: 0 }}>
                                Credit
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.date} · {item.source}</div>
                        </div>
                        <div style={{ ...amountStyle(), fontSize: 13, fontWeight: 700, color: item.is_refund ? '#16a34a' : '#111', flexShrink: 0, marginRight: 8 }}>
                          {item.is_refund ? `−${defaultCurrency} ${fmt(item.amount)}` : `${defaultCurrency} ${fmt(item.amount)}`}
                        </div>
                        <button onClick={() => handleUnmarkItem(item)}
                          style={{ ...BTN_UNMARK, cursor: 'pointer' }}>
                          Unmark
                        </button>
                      </div>
                    ))}
                    {displayedReimbursed.length === 0 && (
                      <div style={{ padding: '10px 8px', color: '#94a3b8', fontSize: 12 }}>No results.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
